/**
 * Centralized haptic feedback (Web Vibration API).
 *
 * IMPORTANT: iOS Safari and iOS PWA standalone do NOT support
 * `navigator.vibrate` — the method is simply absent, so every call is a silent
 * no-op there. Haptics are therefore a PROGRESSIVE ENHANCEMENT (mainly Android
 * + other vibrate-capable browsers); never rely on them as the only feedback
 * channel — always pair with a visual and/or audio cue (toast, flash, beep).
 *
 * All callers go through `haptic(kind)` with a SEMANTIC intent rather than a
 * raw pattern, so the patterns stay consistent app-wide and a single user
 * preference (localStorage "sendly-haptics", default on) gates everything.
 */

import { localStore } from "./safeStorage";

const STORAGE_KEY = "sendly-haptics";

// Vibration patterns keyed by semantic intent. A number = ms of vibration;
// an array = alternating [vibrate, pause, vibrate, …] ms. Kept deliberately
// short for the tap-class intents so rapid actions don't feel buzzy.
const PATTERNS = {
  tap: 12,                  // light acknowledgement (toggle, counter, selection)
  success: 35,              // a state was committed (ascent logged, top/zone saved)
  longpress: 20,            // long-press threshold reached (e.g. map edit)
  warning: [180, 90, 180],  // timer minute warning
  end: [450, 160, 450],     // session end horn
  removed: [50, 40, 50],    // destructive action committed (delete / kick)
};

// Cache the preference so the hot path (every tap) avoids a localStorage read.
let enabledCache = null;

/** Whether the user has haptics enabled (default true). */
export function isHapticsEnabled() {
  if (enabledCache !== null) return enabledCache;
  enabledCache = localStore.get(STORAGE_KEY) !== "false";
  return enabledCache;
}

/** Persist the user's haptics on/off preference and refresh the cache. */
export function setHapticsEnabled(value) {
  enabledCache = !!value;
  localStore.set(STORAGE_KEY, String(!!value));
}

/**
 * Fire a haptic pulse for a semantic intent. No-op when haptics are disabled
 * or the platform lacks the Vibration API (iOS). Never throws.
 */
export function haptic(kind = "tap") {
  try {
    if (!isHapticsEnabled()) return;
    if (typeof navigator === "undefined" || typeof navigator.vibrate !== "function") return;
    navigator.vibrate(PATTERNS[kind] || PATTERNS.tap);
  } catch {}
}
